import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';

const highlights = [
  { value: '10+', label: '年行业经验', icon: 'ri-calendar-check-line' },
  { value: '200+', label: '平台深度评测', icon: 'ri-medal-line' },
  { value: '1500+', label: '原创文章发布', icon: 'ri-article-line' },
  { value: '50万+', label: '累计服务用户', icon: 'ri-user-heart-line' },
];

const values = [
  { title: '客观公正', desc: '不偏不倚，基于真实体验和数据进行评测，不接受付费排名。', icon: 'ri-scales-line' },
  { title: '专业深度', desc: '核心团队拥有10年以上博彩行业研究经验，内容深度专业。', icon: 'ri-stack-line' },
  { title: '安全第一', desc: '将用户资金和隐私安全放在首位，严格筛选平台资质。', icon: 'ri-shield-check-line' },
  { title: '用户至上', desc: '以用户需求为导向，持续优化内容和功能，提升用户体验。', icon: 'ri-heart-line' },
];

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;

    const honeypotEl = form.querySelector<HTMLInputElement>('input[name="phone_alt"]');
    if (honeypotEl && honeypotEl.value.trim() !== '') {
      setFormStatus('success');
      form.reset();
      return;
    }

    const formData = new FormData(form);

    try {
      const response = await fetch('https://readdy.ai/api/form/d9q5737nahb7382ov6d0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed: Record<string, unknown> | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        // not JSON
      }

      if (response.ok && parsed && parsed.code === 'OK') {
        setFormStatus('success');
        setFormError('');
        form.reset();
      } else {
        const serverMsg =
          (parsed as Record<string, unknown> | null)?.meta &&
          typeof (parsed as Record<string, unknown>).meta === 'object'
            ? ((parsed as Record<string, unknown>).meta as Record<string, string>).message || ''
            : '';
        if (serverMsg.includes('spam') || (responseText && responseText.includes('spam'))) {
          setFormStatus('error');
          setFormError('提交失败，请稍后重试');
        } else {
          setFormStatus('error');
          setFormError(serverMsg || '提交失败，请稍后重试');
        }
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请检查网络后重试');
    }
  };

  const schema = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'AboutPage',
        name: '关于我们 - 博彩网',
        url: 'https://bo-cai-wang.com/about',
        description: '博彩网成立于2014年，是国内领先的专业博彩资讯与导航平台。了解我们的品牌故事、核心价值和联系方式。',
        isPartOf: { '@id': 'https://bo-cai-wang.com/#website' },
        inLanguage: 'zh-CN',
      },
      {
        '@type': 'Organization',
        name: '博彩网',
        url: 'https://bo-cai-wang.com',
        description: '专业博彩平台导航与资讯中心，提供最新博彩网站评测、优惠活动、赔率分析及安全指南。',
        foundingDate: '2014',
        contactPoint: {
          '@type': 'ContactPoint',
          contactType: 'customer support',
          email: 'support@bo-cai-wang.com',
          availableLanguage: ['Chinese'],
        },
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'https://bo-cai-wang.com' },
          { '@type': 'ListItem', position: 2, name: '关于我们', item: 'https://bo-cai-wang.com/about' },
        ],
      },
    ],
  };

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schema, null, 2) }} />
      <Navbar />

      <main className="pt-20">
        {/* Breadcrumb */}
        <div className="px-4 md:px-6 py-3 bg-background-100/50 border-b border-background-200/70">
          <div className="max-w-7xl mx-auto flex items-center gap-2 text-sm text-foreground-500">
            <Link to="/" className="hover:text-accent-600 transition-colors">首页</Link>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <span className="text-foreground-900 font-medium">关于我们</span>
          </div>
        </div>

        {/* Hero Banner */}
        <section className="relative py-16 md:py-24 px-4 md:px-6 overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Warm%20elegant%20abstract%20office%20background%20with%20golden%20accents%20and%20soft%20curved%20lines%2C%20dark%20burgundy%20and%20gold%20color%20palette%2C%20corporate%20professional%20atmosphere%2C%20subtle%20geometric%20pattern%2C%20luxurious%20and%20trustworthy%20feel&width=1920&height=500&seq=about-hero-bg&orientation=landscape"
            alt=""
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/40 to-black/55"></div>
          <div className="relative z-10 max-w-4xl mx-auto text-center">
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold font-heading text-background-50 mb-4">
              关于<span className="text-accent-400">博彩网</span>
            </h1>
            <p className="text-sm md:text-base text-background-200/80 max-w-2xl mx-auto leading-relaxed">
              十年专注博彩行业，以专业评测和深度内容服务数百万用户
            </p>
          </div>
        </section>

        {/* Highlights */}
        <section className="py-12 md:py-16 px-4 md:px-6 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
              {highlights.map((h) => (
                <div key={h.label} className="text-center p-5 bg-background-100/70 border border-background-200/70 rounded-lg">
                  <div className="w-10 h-10 mx-auto mb-3 flex items-center justify-center rounded-full bg-accent-100 text-accent-600">
                    <i className={`${h.icon} text-lg`}></i>
                  </div>
                  <div className="text-2xl md:text-3xl font-bold font-heading text-accent-600 mb-1">{h.value}</div>
                  <div className="text-sm text-foreground-600">{h.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Brand Story */}
        <section className="py-12 md:py-16 px-4 md:px-6 bg-background-100/70">
          <div className="max-w-7xl mx-auto">
            <div className="max-w-3xl">
              <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900 mb-6">品牌故事</h2>
              <div className="space-y-4 text-sm md:text-base text-foreground-700 leading-relaxed">
                <p>
                  博彩网成立于2014年，是国内最早专注于在线博彩资讯与导航服务的专业平台之一。创始团队由资深博彩行业分析师、数据科学家和媒体人组成，怀着"让每一位博彩爱好者都能找到正规可靠平台"的初心，开启了这段征程。
                </p>
                <p>
                  十年来，我们始终坚持独立、客观、深度的内容创作原则。平台评测基于真实体验和多维度数据对比，不接受任何平台的付费排名或虚假宣传要求。我们的评测团队每年实地体验和跟踪评测超过200家博彩平台，累计发布原创文章超过1500篇。
                </p>
                <p>
                  截至目前，博彩网已累计服务超过50万用户，日均访问量超过10万人次。我们的内容被众多主流媒体和行业机构引用，已成为中文博彩资讯领域最具影响力的平台之一。
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Core Values */}
        <section className="py-12 md:py-16 px-4 md:px-6 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900 mb-6 text-center">核心价值观</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {values.map((v) => (
                <div key={v.title} className="bg-background-100/70 border border-background-200/70 rounded-lg p-6 text-center">
                  <div className="w-12 h-12 mx-auto mb-4 flex items-center justify-center rounded-full bg-accent-100 text-accent-600">
                    <i className={`${v.icon} text-xl`}></i>
                  </div>
                  <h3 className="text-base font-semibold text-foreground-900 mb-2">{v.title}</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">{v.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact Section */}
        <section className="py-12 md:py-16 px-4 md:px-6 bg-background-100/70">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
              {/* Contact Info */}
              <div>
                <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900 mb-6">联系我们</h2>
                <p className="text-sm md:text-base text-foreground-600 mb-6 leading-relaxed">
                  如果您有任何问题、建议或合作意向，欢迎通过以下方式联系我们。我们的团队将在24小时内回复您。
                </p>
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 flex-shrink-0">
                      <i className="ri-mail-line"></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-900">电子邮箱</p>
                      <p className="text-sm text-foreground-600">support@bo-cai-wang.com</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 flex-shrink-0">
                      <i className="ri-wechat-line"></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-900">微信公众号</p>
                      <p className="text-sm text-foreground-600">博彩网官方</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 flex-shrink-0">
                      <i className="ri-time-line"></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-900">工作时间</p>
                      <p className="text-sm text-foreground-600">周一至周五 9:00 - 18:00</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Contact Form */}
              <div className="bg-background-50 border border-background-200/70 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-foreground-900 mb-4 font-heading">发送消息</h3>
                <form
                  data-readdy-form
                  id="about-contact-form"
                  onSubmit={handleSubmit}
                  className="space-y-4"
                >
                  <div>
                    <label htmlFor="about-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      您的姓名
                    </label>
                    <input
                      type="text"
                      id="about-name"
                      name="name"
                      required
                      className="w-full px-3 py-2.5 border border-background-300 rounded-md text-sm bg-background-50 text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-accent-400 focus:ring-1 focus:ring-accent-400 transition-colors"
                      placeholder="请输入您的姓名"
                    />
                  </div>

                  <div>
                    <label htmlFor="about-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      电子邮箱
                    </label>
                    <input
                      type="email"
                      id="about-email"
                      name="email"
                      required
                      className="w-full px-3 py-2.5 border border-background-300 rounded-md text-sm bg-background-50 text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-accent-400 focus:ring-1 focus:ring-accent-400 transition-colors"
                      placeholder="请输入您的邮箱"
                    />
                  </div>

                  <div>
                    <label htmlFor="about-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      留言内容
                    </label>
                    <textarea
                      id="about-message"
                      name="message"
                      rows={4}
                      required
                      maxLength={500}
                      className="w-full px-3 py-2.5 border border-background-300 rounded-md text-sm bg-background-50 text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-accent-400 focus:ring-1 focus:ring-accent-400 transition-colors resize-none"
                      placeholder="请输入您的留言（500字以内）"
                    />
                  </div>

                  <input
                    type="text"
                    name="phone_alt"
                    className="honeypot-field"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                  />

                  {formStatus === 'success' && (
                    <div className="p-3 bg-accent-50 text-accent-700 rounded-md text-sm">
                      消息已发送成功，我们会尽快回复您！
                    </div>
                  )}
                  {formStatus === 'error' && (
                    <div className="p-3 bg-accent-50 text-accent-700 rounded-md text-sm">
                      {formError}
                    </div>
                  )}

                  <button
                    type="submit"
                    className="w-full py-2.5 bg-accent-500 text-background-50 rounded-md text-sm font-medium hover:bg-accent-600 transition-colors duration-200 whitespace-nowrap"
                  >
                    提交留言
                  </button>
                </form>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}